import * as Yup from 'yup';
import { AutocompleteOption, PoolAutocomplete, PoolBanner, PoolFormikNumberTextfield } from '@synergy/components-next';
import { Box, Stack } from '@mui/material';
import { Field, FieldProps, Formik, FormikHelpers, FormikProps } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';
import { PoolButton, PoolModal, ValueOrNDash } from '@synergy/components';
import { ReactElement, useEffect, useRef } from 'react';
import { fields } from '@synergy/utils';

import * as ExportInvoicesActions from '../store/invoice-actions';
import * as InvoiceNumberSequenceActions from '../../../configuration/finance/invoice-number-sequence/store/invoice-number-sequence-actions';
import { ExportInvoicesModalNotifications } from './export-invoices-modal-notifications';
import { Prefix } from '../../../../common/models/prefix';
import { invoiceNumberSequenceHistorySelector } from '../../../configuration/finance/invoice-number-sequence/store/invoice-number-sequence-selector';
import { latestExportSelector } from '../../invoice-selectors';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

export interface ExportInvoicesFormValues {
    prefix: Prefix | null;
    fromNumber: number | null;
    toNumber: number | null;
}

const fieldNames = fields<ExportInvoicesFormValues>();

export interface ExportInvoicesModalProps {
    isVisible: boolean;
    onClose: () => void;
}

export const ExportInvoicesModal = (props: ExportInvoicesModalProps): ReactElement => {
    const dispatch = useThunkDispatch();
    const intl = useIntl();
    const latestExport = useAppSelector(latestExportSelector);
    const prefixes = useAppSelector(invoiceNumberSequenceHistorySelector);
    const formikRef = useRef<FormikProps<ExportInvoicesFormValues> | null>(null);

    useEffect(() => {
        if (props.isVisible) {
            dispatch(InvoiceNumberSequenceActions.fetchInvoiceNumberSequenceHistory());
        }

        return () => {
            dispatch(ExportInvoicesActions.resetLatestExport());
        };
    }, [dispatch, props.isVisible]);

    useEffect(() => {
        if (!formikRef.current) {
            return;
        }
        // TODO: #1531 https://gitlab.com/fusonic/pool/synergy/portal/-/issues/1531
        formikRef.current.setValues({
            prefix: formikRef.current.values.prefix,
            fromNumber:
                latestExport?.highestReceiptNumber !== undefined
                    ? latestExport.highestReceiptNumber + 1
                    : ('' as unknown as number),
            toNumber: '' as unknown as number,
        });
    }, [latestExport]);

    useEffect(() => {
        if (!formikRef.current) {
            return;
        }

        const prefix = prefixes.find(prefix => prefix.isDefault) ?? null;

        formikRef.current.setValues({
            prefix,
            fromNumber: formikRef.current.values.fromNumber,
            toNumber: formikRef.current.values.toNumber,
        });

        if (prefix) {
            dispatch(ExportInvoicesActions.fetchLatestExport(prefix));
        }
    }, [prefixes, dispatch]);

    const handleSubmit = (
        values: ExportInvoicesFormValues,
        formikHelpers: FormikHelpers<ExportInvoicesFormValues>
    ): void => {
        dispatch(
            ExportInvoicesActions.exportInvoices({
                fromNumber: values.fromNumber,
                toNumber: (values.toNumber as unknown as string) === '' ? null : values.toNumber,
                // prefix is required in the validation schema and can't be null here
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-non-null-asserted-optional-chain
                invoiceNumberSequenceHistoryId: values.prefix?.id!,
            })
        );
        handleClose(formikHelpers);
    };

    const initialValues: ExportInvoicesFormValues = {
        prefix: null,
        fromNumber:
            latestExport?.highestReceiptNumber !== undefined
                ? latestExport.highestReceiptNumber + 1
                : ('' as unknown as number),
        toNumber: '' as unknown as number,
    };

    const validationSchema = Yup.object().shape(
        {
            [fieldNames.prefix]: Yup.mixed().required('app.error.required'),
            [fieldNames.fromNumber]: Yup.number()
                .typeError('app.error.required')
                .required('app.error.required')
                .moreThan(0, 'app.error.greaterZero'),
            [fieldNames.toNumber]: Yup.number()
                .nullable(true)
                .min(Yup.ref(fieldNames.fromNumber), 'app.error.minFromNumber'),
        },
        [[fieldNames.fromNumber, fieldNames.toNumber]]
    );

    const handleClose = (
        formikProps: FormikProps<ExportInvoicesFormValues> | FormikHelpers<ExportInvoicesFormValues>
    ): void => {
        formikProps.resetForm();
        props.onClose();
    };

    const handlePrefixChange = (form: FormikProps<ExportInvoicesFormValues>) => (prefix: Prefix | null) => {
        form.setFieldValue(fieldNames.prefix, prefix);

        if (prefix) {
            dispatch(ExportInvoicesActions.fetchLatestExport(prefix));
        } else {
            dispatch(ExportInvoicesActions.resetLatestExport());
        }
    };

    const options: AutocompleteOption<Prefix>[] = prefixes.map(prefix => {
        const text = prefix.prefix?.length > 0 ? prefix.prefix : '-';

        let displayValue = text;
        if (prefix.isDefault) {
            displayValue = `${text} (${intl.formatMessage({ id: 'app.common.standard' })})`;
        }
        if (prefix.wasDefault) {
            displayValue = `${text} (${intl.formatMessage({ id: 'app.export.invoicesModal.wasStandard' })})`;
        }
        return {
            value: prefix,
            displayValue,
        };
    });

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
            innerRef={formikRef}
            enableReinitialize={false}
        >
            {formikProps => (
                <PoolModal
                    title={<FormattedMessage id="app.export.invoicesModal" />}
                    isVisible={props.isVisible}
                    handleClose={() => handleClose(formikProps)}
                    actionChildren={
                        <>
                            <PoolButton styleType="firstLevel" variant="text" onClick={() => handleClose(formikProps)}>
                                <FormattedMessage id="app.common.cancel" />
                            </PoolButton>
                            <PoolButton
                                styleType="firstLevel"
                                disabled={!latestExport?.finishedDate && !!latestExport?.startedDate}
                                onClick={() => formikProps.handleSubmit()}
                            >
                                <FormattedMessage id="app.common.export" />
                            </PoolButton>
                        </>
                    }
                >
                    <Stack spacing={3}>
                        <Stack spacing={1}>
                            <Stack spacing={2}>
                                <Field name={fieldNames.prefix}>
                                    {({ form, meta }: FieldProps<Prefix>) => (
                                        <PoolAutocomplete
                                            options={options}
                                            label={<FormattedMessage id="app.export.invoicesModal.prefix" />}
                                            required={true}
                                            error={meta.touched && !!meta.error}
                                            value={form.values.prefix}
                                            onChange={handlePrefixChange(form)}
                                            helperText={
                                                meta.error && meta.touched && <FormattedMessage id={meta.error} />
                                            }
                                        />
                                    )}
                                </Field>
                                <Stack direction="row" spacing={2}>
                                    <PoolFormikNumberTextfield
                                        name={fieldNames.fromNumber}
                                        label={<FormattedMessage id="app.export.invoicesModal.fromNumber" />}
                                        fullWidth={true}
                                        required={true}
                                    />
                                    <PoolFormikNumberTextfield
                                        name={fieldNames.toNumber}
                                        label={<FormattedMessage id="app.export.invoicesModal.toNumber" />}
                                        fullWidth={true}
                                    />
                                </Stack>
                            </Stack>

                            <Box component="span" color="grey.700" typography="caption">
                                <FormattedMessage
                                    id="app.export.invoicesModal.highestInvoiceNumber"
                                    values={{
                                        highestInvoiceNumber: (
                                            <ValueOrNDash value={latestExport?.highestReceiptNumber} />
                                        ),
                                    }}
                                />
                            </Box>
                        </Stack>

                        <Stack spacing={2}>
                            {formikProps.values.prefix ? (
                                <PoolBanner variant="outlined">
                                    <FormattedMessage id="app.export.invoicesModal.infoBanner" />
                                </PoolBanner>
                            ) : (
                                <PoolBanner variant="outlined">
                                    <FormattedMessage id="app.export.invoicesModal.selectPrefix" />
                                </PoolBanner>
                            )}
                            <ExportInvoicesModalNotifications latestExport={latestExport} />
                        </Stack>
                    </Stack>
                </PoolModal>
            )}
        </Formik>
    );
};
